/**
 * `useWorkspaces` — TanStack Query hook for the caller's workspace
 * list (`GET /api/account/me/workspaces`, IA-2.1 commit `044ffd0`).
 *
 * Consumed by `<WorkspaceSwitcher/>` (IA-2.2) and the AppShell
 * route-restoration flow (IA-2.3). The single shared `workspacesKey`
 * helper keeps every consumer aligned on the same cache shape per the
 * Wave 1.5 G-CC2 lesson — no per-consumer key drift.
 *
 * `staleTime: 60_000` matches the IA-redesign §3.1 expectation that
 * the dropdown reflects the live membership list within a minute of a
 * change (joining a new team, being removed). Workspace lists rarely
 * churn intra-session; stale within 60s is fine.
 *
 * The hook is `enabled: Boolean(accountId)` so a logged-out user (no
 * dev-shim id, no Clerk session) doesn't fire a 401 fetch on every
 * mount.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { workspacesKey } from "../lib/workspacesKey";
import { apiFetch } from "./client";
import type { components } from "./schema.gen";

export type WorkspaceRow = components["schemas"]["WorkspaceRow"];

const ENDPOINT = "/api/account/me/workspaces";
const STALE_TIME_MS = 60_000;

export function useWorkspaces() {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: workspacesKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    queryFn: () => apiFetch<WorkspaceRow[]>(ENDPOINT),
  });
}
